import React, { useState, useEffect } from "react";
import Slider from "react-slick";
import Typography from "@components/typography";
import Column from "@components/column";
import Row from "@components/row";
import {
  Star,
  Phone,
  Mail,
  MapPin,
  Clock,
  FileText,
  Users,
  Truck,
  Shield,
  Award,
  Heart,
  Package,
  ShoppingCart,
  DollarSign,
  Calendar,
  BarChart3,
  CreditCard,
  ShieldCheck,
  MessageCircle,
  Eye,
  Share2,
  Download,
  Navigation,
  ExternalLink,
  ZoomOut,
  ZoomIn,
  RotateCw,
  Copy,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  LogIn,
  Wallet,
} from "lucide-react";
import { useModal } from "@context/ModalContext";
import WalletAdjustmentModal from "@components/modals/WalletAdjustmentModal";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import * as S from "./style";
import Fields from "@components/fields";
import { Form, Formik } from "formik";
import OverviewCard from "@commons/cards/overview";
import Grid from "@components/grid";
import CountCard from "@commons/cards/count";
import { countConfig } from "@pages/admin/dashboard";
import { useNavigate } from "react-router-dom";
import { Helper } from "@utils/index";
import useConfig from "@hooks/useConfig";
import OrderStatusCards from "@commons/orders/OrderStatusCards";
import NotificationApi from "@apis/notification";
import { Bell } from "lucide-react";
import StoreOrderStats from "./StoreOrderStats";
import StoreOverviewDashboard from "./StoreOverviewDashboard";
import StoreBankDetails from "./StoreBankDetails";
import RecentNotifications from "@components/recent-notifications";
import UserApi from "@apis/user";
import PushNotificationForm from "@commons/push-notifications/form";

const Overview = ({ state }: { state: any }) => {
  const navigate = useNavigate();
  const { showModal, hideModal } = useModal();
  const { settings: currencySettings } = useGeneralSettings();
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const { bucketUrl } = useConfig();
  if (!state) return <Typography>No data available</Typography>;

  // Custom arrow components for slider
  const NextArrow = (props: any) => {
    const { onClick } = props;
    return (
      <S.SliderArrow right onClick={onClick}>
        <ChevronRight size={24} />
      </S.SliderArrow>
    );
  };

  const PrevArrow = (props: any) => {
    const { onClick } = props;
    return (
      <S.SliderArrow onClick={onClick}>
        <ChevronLeft size={24} />
      </S.SliderArrow>
    );
  };

  const {
    storeName,
    storeEmail,
    storePhoneNo,
    city,
    latitude,
    longitude,
    plan,
    module,
    availabilities,
    coverImg,
    avatar,
    registrationImg,
    panImg,
    isFeatured,
    isPopular,
    estimatedDeliveryTime,
    googleMapsLink,
    coverImages,
  } = state;
  console.log({ coverImages });
  // Base URL for images
  const baseURL = bucketUrl + "/";

  // Store metrics data
  const [storeMetrics] = useState({
    totalSales: 125430,
    monthlyGrowth: 12.5,
    activeCustomers: 1247,
    totalOrders: 2894,
    deliveryPartners: 8,
    satisfactionRate: 4.7,
    completionRate: 96.3,
    averageOrderValue: 43.2,
    returningCustomers: 68,
    inventoryItems: 245,
    lowStockItems: 12,
    monthlyVisitors: 15420,
    conversionRate: 18.7,
  });

  // Recent activities
  const recentActivities = [
    { action: "New order placed", time: "2 mins ago", type: "order" },
    { action: "Product review received", time: "15 mins ago", type: "review" },
    { action: "Inventory updated", time: "1 hour ago", type: "inventory" },
    { action: "Payment received", time: "2 hours ago", type: "payment" },
    { action: "Customer support ticket", time: "3 hours ago", type: "support" },
  ];

  const getAvailabilityStatus = (availability: any) => {
    if (availability.is24Hour) return "Open 24 hours";
    if (availability.isClosed) return "Closed";
    if (availability.openTime && availability.closeTime)
      return `${availability.openTime} - ${availability.closeTime}`;
    return "Not set";
  };

  const getStatusColor = (availability: any) => {
    if (availability.is24Hour) return "#10b981";
    if (availability.isClosed) return "#ef4444";
    if (availability.openTime && availability.closeTime) return "#3b82f6";
    return "#6b7280";
  };

  // Copy to clipboard function
  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  // Contact Store Modal Component
  const ContactStoreModal = () => {
    const contactMethods = [
      {
        icon: Phone,
        label: "Phone Number",
        value: storePhoneNo,
        type: "phone",
        action: `tel:${storePhoneNo}`,
      },
      {
        icon: Mail,
        label: "Email Address",
        value: storeEmail,
        type: "email",
        action: `mailto:${storeEmail}`,
      },
      {
        icon: MapPin,
        label: "Store Address",
        value: city,
        type: "address",
        action: googleMapsLink,
      },
      {
        icon: Clock,
        label: "Estimated Delivery",
        value: estimatedDeliveryTime || "30-45 minutes",
        type: "info",
      },
    ];

    return (
      <S.ContactModalContainer>
        <S.ContactModalHeader>
          <S.StoreAvatar>
            <img
              src={avatar ? baseURL + avatar : "/default-avatar.jpg"}
              alt={storeName}
            />
          </S.StoreAvatar>
          <S.ContactStoreInfo>
            <Typography weight={700} size={20}>
              Contact {storeName}
            </Typography>
            <Typography size={14} color="#64748b">
              Get in touch with the store directly
            </Typography>
          </S.ContactStoreInfo>
        </S.ContactModalHeader>

        <S.ContactMethods>
          {contactMethods.map((method, index) => (
            <S.ContactMethod key={index}>
              <S.ContactMethodIcon>
                <method.icon size={20} />
              </S.ContactMethodIcon>
              <S.ContactMethodContent>
                <S.ContactMethodLabel>{method.label}</S.ContactMethodLabel>
                <S.ContactMethodValue>{method.value}</S.ContactMethodValue>
              </S.ContactMethodContent>
              <S.ContactMethodActions>
                {method.action && (
                  <S.ContactActionButton
                    href={method.action}
                    target={
                      method.type === "email" || method.type === "phone"
                        ? "_self"
                        : "_blank"
                    }
                    rel="noopener noreferrer"
                  >
                    {method.type === "phone" && <Phone size={16} />}
                    {method.type === "email" && <Mail size={16} />}
                    {method.type === "address" && <ExternalLink size={16} />}
                  </S.ContactActionButton>
                )}
                <S.CopyButton
                  onClick={() => copyToClipboard(method.value, method.label)}
                  copied={copiedField === method.label}
                >
                  {copiedField === method.label ? (
                    <CheckCircle size={16} />
                  ) : (
                    <Copy size={16} />
                  )}
                </S.CopyButton>
              </S.ContactMethodActions>
            </S.ContactMethod>
          ))}
        </S.ContactMethods>

        {/* <S.StoreHours>
          <S.SectionHeader>
            <Clock size={18} />
            <Typography weight={600} size={16}>
              Store Hours
            </Typography>
          </S.SectionHeader>
          {availabilities && availabilities.length > 0 ? (
            <S.HoursList>
              {availabilities.map((availability: any, index: number) => (
                <S.HourItem key={index}>
                  <S.DayLabel>{availability.day}</S.DayLabel>
                  <S.TimeSlot color={getStatusColor(availability)}>
                    {getAvailabilityStatus(availability)}
                  </S.TimeSlot>
                </S.HourItem>
              ))}
            </S.HoursList>
          ) : (
            <Typography size={14} color="#64748b" align="center">
              Store hours not specified
            </Typography>
          )}
        </S.StoreHours> */}

        <S.ContactFooter>
          <Typography size={12} color="#94a3b8" align="center">
            Store is currently{" "}
            {availabilities?.[0]?.isClosed ? "closed" : "open"} • Response time:
            Usually within 15 minutes
          </Typography>
        </S.ContactFooter>
      </S.ContactModalContainer>
    );
  };

  const handleContactStore = () => {
    showModal(<ContactStoreModal />, {
      width: "500px",
    });
  };

  // Document viewer modal component
  const DocumentViewer = ({
    documentUrl,
    documentName,
  }: {
    documentUrl: string;
    documentName: string;
  }) => {
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);

    const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.25, 3));
    const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.25, 0.5));
    const handleRotate = () => setRotation((prev) => (prev + 90) % 360);
    const handleReset = () => {
      setZoom(1);
      setRotation(0);
    };

    return (
      <S.DocumentModalContainer>
        <S.DocumentModalHeader>
          <Typography weight={600} size={18}>
            {documentName}
          </Typography>
          <S.DocumentControls>
            <S.ControlButton onClick={handleZoomOut} disabled={zoom <= 0.5}>
              <ZoomOut size={18} />
            </S.ControlButton>
            <S.ControlButton onClick={handleZoomIn} disabled={zoom >= 3}>
              <ZoomIn size={18} />
            </S.ControlButton>
            <S.ControlButton onClick={handleRotate}>
              <RotateCw size={18} />
            </S.ControlButton>
            <S.ControlButton onClick={handleReset}>Reset</S.ControlButton>
            <S.ZoomLevel>{Math.round(zoom * 100)}%</S.ZoomLevel>
          </S.DocumentControls>
        </S.DocumentModalHeader>
        <S.DocumentImageContainer>
          <S.DocumentImage
            src={baseURL + documentUrl}
            alt={documentName}
            style={{
              transform: `scale(${zoom}) rotate(${rotation}deg)`,
            }}
          />
        </S.DocumentImageContainer>
      </S.DocumentModalContainer>
    );
  };

  const handleViewDocument = (documentUrl: string, documentName: string) => {
    showModal(
      <DocumentViewer documentUrl={documentUrl} documentName={documentName} />,
      { width: "500px" },
    );
  };

  // Location viewer modal component
  const LocationViewer = () => {
    const selectedPoint =
      latitude && longitude
        ? {
            lat: parseFloat(latitude),
            lng: parseFloat(longitude),
            mapsUrl: googleMapsLink,
          }
        : null;

    return (
      <Formik initialValues={{}} onSubmit={() => {}}>
        <Form>
          <S.LocationModalContainer>
            <S.LocationModalHeader>
              <Typography weight={600} size={18}>
                Store Location
              </Typography>
              <S.LocationInfo>
                <S.LocationDetail>
                  <MapPin size={16} />
                  <Typography size={14}>
                    {storeName}, {city}
                  </Typography>
                </S.LocationDetail>
                {selectedPoint && (
                  <S.LocationDetail>
                    <Typography size={12} color="#64748b">
                      {selectedPoint.lat.toFixed(6)},{" "}
                      {selectedPoint.lng.toFixed(6)}
                    </Typography>
                  </S.LocationDetail>
                )}
              </S.LocationInfo>
            </S.LocationModalHeader>
            <S.MapContainer>
              <Fields.Map
                name="location_view"
                height="400px"
                disabled={true}
                selectedPoint={selectedPoint}
                onSelectPoint={() => {}} // Empty function for read-only mode
              />
            </S.MapContainer>
            {googleMapsLink && (
              <S.LocationActions>
                <S.LocationActionButton
                  href={googleMapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Navigation size={16} />
                  Open in Google Maps
                </S.LocationActionButton>
              </S.LocationActions>
            )}
          </S.LocationModalContainer>
        </Form>
      </Formik>
    );
  };

  const handleViewLocation = () => {
    showModal(<LocationViewer />, { width: "800px" });
  };

  const DocumentsListModal = () => (
    <S.DocumentModalContainer>
      <S.DocumentModalHeader>
        <Typography weight={600} size={18}>
          Store Documents
        </Typography>
      </S.DocumentModalHeader>
      <S.DocumentsGrid style={{ padding: "24px" }}>
        <S.DocumentItem>
          <S.DocumentIcon>
            <FileText size={20} />
          </S.DocumentIcon>
          <S.DocumentInfo>
            <S.DocumentContent>
              <S.DocumentLabel>Business Registration</S.DocumentLabel>
              <S.DocumentStatus verified>Verified</S.DocumentStatus>
            </S.DocumentContent>
            <S.DocumentAction
              onClick={() =>
                handleViewDocument(registrationImg, "Business Registration")
              }
            >
              View
            </S.DocumentAction>
          </S.DocumentInfo>
        </S.DocumentItem>
        <S.DocumentItem>
          <S.DocumentIcon>
            <Shield size={20} />
          </S.DocumentIcon>
          <S.DocumentInfo>
            <S.DocumentContent>
              <S.DocumentLabel>PAN Certificate</S.DocumentLabel>
              <S.DocumentStatus verified>Verified</S.DocumentStatus>
            </S.DocumentContent>
            <S.DocumentAction
              onClick={() => handleViewDocument(panImg, "PAN Certificate")}
            >
              View
            </S.DocumentAction>
          </S.DocumentInfo>
        </S.DocumentItem>
      </S.DocumentsGrid>
    </S.DocumentModalContainer>
  );

  const handleOpenDocumentsKeywords = () => {
    showModal(<DocumentsListModal />, { width: "500px" });
  };
  const user = Helper.getUser();

  const handleLoginAsStore = async () => {
    try {
      const res = await UserApi.loginAs({
        id: state.id,
        data: { role: "store" },
      });
      if (res?.token) {
        Helper.saveUser(res);
        window.open(`${window.location.origin}/store`, "_blank");
      }
    } catch (error) {
      console.error("Failed to login as store", error);
    }
  };

  const handleLoadWallet = () => {
    showModal(
      <WalletAdjustmentModal
        user={state}
        onClose={hideModal}
        onSuccess={() => {
          window.location.reload();
        }}
        currencySettings={currencySettings}
      />,
    );
  };

  return (
    <S.Container>
      {/* Store Header Section */}
      <S.StoreHeaderSection>
        <S.CoverImageContainer>
          {coverImages && coverImages.length > 0 ? (
            <Slider
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              autoplay={true}
              autoplaySpeed={3000}
              arrows={true}
              nextArrow={<NextArrow />}
              prevArrow={<PrevArrow />}
            >
              {coverImages.map((img: any, index: number) => (
                <S.CoverImage
                  key={index}
                  src={
                    img.coverImages
                      ? baseURL + img.coverImages
                      : "/default-cover.jpg"
                  }
                  alt={`Store cover ${index + 1}`}
                />
              ))}
            </Slider>
          ) : (
            <S.CoverImage
              src={coverImg ? baseURL + coverImg : "/default-cover.jpg"}
              alt="Store cover"
            />
          )}
          <S.HeaderOverlay />

          <S.CoverActionIcon
            onClick={handleOpenDocumentsKeywords}
            title="View Documents"
          >
            <FileText size={24} />
          </S.CoverActionIcon>
          <S.StoreHeaderContent>
            <S.AvatarContainer>
              {avatar ? (
                <S.AvatarImage src={baseURL + avatar} alt="Store avatar" />
              ) : (
                <S.AvatarPlaceholder
                  bg={Helper.getDynamicColor(storeName || "Store")}
                >
                  {Helper.getInitials(storeName || "Store")}
                </S.AvatarPlaceholder>
              )}
              <S.OnlineIndicator />
            </S.AvatarContainer>
            <S.StoreInfo>
              <S.StoreNameRow>
                <Typography weight={700} size={28} color="white">
                  {storeName}
                </Typography>
                <S.BadgeContainer>
                  {isFeatured && <S.FeaturedBadge>Featured</S.FeaturedBadge>}
                  {isPopular && <S.PopularBadge>Popular</S.PopularBadge>}
                  <S.VerificationBadge>
                    <ShieldCheck size={14} />
                    Verified
                  </S.VerificationBadge>
                </S.BadgeContainer>
              </S.StoreNameRow>

              <S.StoreMeta>
                <S.MetaItem>
                  <Star size={16} color="#ffd700" />
                  <Typography size={14} color="white">
                    {storeMetrics.satisfactionRate}/5 (
                    {storeMetrics.activeCustomers} reviews)
                  </Typography>
                </S.MetaItem>
                <S.MetaItem>
                  <Clock size={16} color="white" />
                  <Typography size={14} color="white">
                    {estimatedDeliveryTime || "30-45 mins"}
                  </Typography>
                </S.MetaItem>
                <S.MetaItem>
                  <Award size={16} color="white" />
                  <Typography size={14} color="white">
                    {plan} Plan • {module?.name}
                  </Typography>
                </S.MetaItem>
                <S.MetaItem>
                  <MapPin size={16} color="white" />
                  <Typography size={14} color="white">
                    {city}
                  </Typography>
                </S.MetaItem>
              </S.StoreMeta>

              <S.ActionButtons>
                <S.ActionButton primary onClick={handleContactStore}>
                  <MessageCircle size={16} />
                  Contact Store
                </S.ActionButton>
                <S.ActionButton onClick={handleViewLocation}>
                  <MapPin size={16} />
                  View Location
                </S.ActionButton>
                <S.ActionButton
                  onClick={() => {
                    if (user.role === "store") {
                      navigate(`/store/users/update-store/${state.id}`, {
                        state,
                      });
                    } else
                      navigate(`/admin/users/store/update-store/${state.id}`, {
                        state,
                      });
                  }}
                >
                  <Share2 size={16} />
                  Edit Store
                </S.ActionButton>
                <S.ActionButton
                  onClick={() => {
                    showModal(
                      <PushNotificationForm
                        initialData={{
                          targetAudience: "store",
                          targetId: state.id,
                          title: `Store Notification: ${storeName}`,
                        }}
                        onSuccess={() => hideModal()}
                      />,
                      { width: "1000px" },
                    );
                  }}
                >
                  <Bell size={16} />
                  Send Push
                </S.ActionButton>
                <S.ActionButton
                  onClick={() => {
                    navigate(`/admin/chat?userId=${state.id}&role=store`);
                  }}
                >
                  <MessageCircle size={16} />
                  Chat
                </S.ActionButton>
                {user?.role === "admin" && (
                  <>
                    <S.ActionButton primary onClick={handleLoginAsStore}>
                      <LogIn size={16} />
                      Login as Store
                    </S.ActionButton>
                    <S.ActionButton onClick={handleLoadWallet}>
                      <Wallet size={16} />
                      Load Wallet
                    </S.ActionButton>
                  </>
                )}
              </S.ActionButtons>
            </S.StoreInfo>
          </S.StoreHeaderContent>
        </S.CoverImageContainer>
      </S.StoreHeaderSection>

      <S.ContentGrid>
        <S.LeftColumn>
          {/* Store Overview Dashboard */}
          <StoreOverviewDashboard storeId={state.id} />

          {/* Store Bank Details */}
          <StoreBankDetails storeId={state.id} />
        </S.LeftColumn>

        <S.RightColumn>
          {/* Recent Notifications */}
          <S.Section>
            <RecentNotifications userId={state?.id} role="store" />
          </S.Section>
        </S.RightColumn>
      </S.ContentGrid>
    </S.Container>
  );
};

export default Overview;
